import { timestampToDateString } from '@/utils'
import dayjs from 'dayjs'
import locale from 'dayjs/locale/cs'
import weekday from 'dayjs/plugin/weekday'
import { eqBy } from 'ramda'
import { useMemo, useState } from 'react'
import { Typography } from '../../atoms'
import { MonthSelect } from '../MonthSelect'
import { makeDates, DateItem } from './helpers'
import { Container, DateCell, DatesContainer } from './styles'

dayjs.extend(weekday)
dayjs.locale(locale)

export interface CalendarProps {
  value?: number
  onChange: (value: number) => void
  minDate?: Date
  maxDate?: Date
}

interface MonthYearValue {
  month: number
  year: number
}

export const Calendar = ({
  value,
  onChange,
  minDate = new Date('1900'),
  maxDate = new Date(),
}: CalendarProps) => {
  const date = useMemo(() => {
    if (value || value === 0) return new Date(value)

    return new Date()
  }, [value])

  const [month, setMonth] = useState(date.getMonth() + 1)
  const [year, setYear] = useState(date.getFullYear())

  const days = useMemo(
    () =>
      makeDates({
        month,
        year,
        minDate: minDate.getTime(),
        maxDate: maxDate.getTime(),
      }),
    [month, year, minDate, maxDate],
  )

  return (
    <Container>
      <MonthSelect
        maxDate={maxDate}
        minDate={minDate}
        onChange={(val: Partial<MonthYearValue>) => {
          if (val.month && val.month !== month) {
            setMonth(val.month)
          }
          if (val.year && val.year !== year) {
            setYear(val.year)
          }
        }}
        value={{ month, year }}
      />
      <DatesContainer>
        {days.map((item: DateItem, index: number) => {
          // type override as not to change the current implementation during migration
          const currentValue = value as number
          return item.value !== '' ? (
            <DateCell
              key={`date-${index}`}
              disabled={item.disabled}
              onClick={() => {
                if (!item.disabled && typeof item.value === 'number')
                  onChange(item.value)
              }}
              selected={
                typeof item.value === 'number' &&
                eqBy(timestampToDateString, item.value, currentValue)
              }
            >
              <Typography variant="p1Body">{item.text}</Typography>
            </DateCell>
          ) : (
            <div key={`empty-${index}`} />
          )
        })}
      </DatesContainer>
    </Container>
  )
}
